import { useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { GraphQLErrors } from '@atlassian/jira-business-graphql-errors/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFieldsValuesActions } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { QUERY } from './gql.tsx';
import type { Response } from './types.tsx';

const QUERY_NAME = 'JWMCalendarUpdateDates';

export type UpdateDatesProps = {
	issueKey: IssueKey;
	issueId: number;
	newStartDate?: string | null;
	newDueDate: string | null;
};

export class NotFoundError extends ValidationError {
	constructor() {
		super('Issue not found');
	}
}

export const useUpdateDates = () => {
	const cloudId = useCloudId();
	const [, { setFieldValue }] = useFieldsValuesActions();
	const { data: startDateField } = useStartDateField();

	return useCallback(
		async ({ issueKey, issueId, newStartDate, newDueDate }: UpdateDatesProps) => {
			try {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const variables = {
					input: {
						issueId: createAri({
							resourceOwner: 'jira',
							cloudId,
							resourceType: 'issue',
							resourceId: String(issueId),
						}),
						operation: 'SET',
					},
				} as any; // eslint-disable-line @typescript-eslint/no-explicit-any

				if (newStartDate != null) {
					variables.input.startDate = {
						fieldId: 'startdate',
						date: {
							formattedDate: formatInTimeZone(new Date(newStartDate), 'UTC', 'dd/MMM/yyyy'),
						},
					};
				}

				if (newDueDate != null) {
					variables.input.dueDate = {
						fieldId: 'duedate',
						date: {
							formattedDate: formatInTimeZone(new Date(newDueDate), 'UTC', 'dd/MMM/yyyy'),
						},
					};
				}

				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const response = (await performPostRequest(`/rest/gira/1/?operation=${QUERY_NAME}`, {
					body: JSON.stringify({
						query: QUERY,
						variables,
					}),
				})) as Response;

				if (response.data?.scheduleTimelineJwmItem == null) {
					const errors = response.errors?.[0];
					const statusCode: number | undefined = errors?.extensions?.statusCode;
					if (statusCode === 404) {
						throw new NotFoundError();
					} else if (
						errors &&
						statusCode &&
						statusCode >= 400 &&
						statusCode < 500 &&
						// This ensures that there is a message from the backend when ValidationError is thrown
						errors.message.length > 0
					) {
						throw new ValidationError(errors.message);
					} else {
						throw new GraphQLErrors(errors);
					}
				}

				const result = response.data.scheduleTimelineJwmItem;

				if (!result.success) {
					throw new GraphQLErrors(result.errors);
				}

				setFieldValue(issueKey, 'duedate', newDueDate);
				if (startDateField && newStartDate != null) {
					setFieldValue(issueKey, startDateField.id, newStartDate);
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: QUERY_NAME,
						packageName: 'jiraBusinessCalendar',
						teamName: 'jira-yurree',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});

				throw error;
			}
		},
		[cloudId, setFieldValue, startDateField],
	);
};
